import React, { useState, useEffect, useCallback } from 'react'
import api from '../utils/api'
import {
  Table,
  Button,
  Space,
  Modal,
  message,
  Tag,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Divider,
  List,
  InputNumber,
  Switch,
  Tabs
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import {
  getRevisitReminderConfigs,
  deleteRevisitReminderConfig,
  previewRevisitReminderRecipients,
  sendRevisitReminder
} from '../api/revisitReminder'
import RevisitReminderLogs from '../components/RevisitReminderLogs'
import Hint from '../components/common/Hint'
import { formatTz } from '../utils/timezone'


/**
 * 全店的回訪提醒頻率上限。
 *
 * 單一設定的「最多提醒幾次」管得住同一個自動化重複發，管不住店家同時開了
 * 回訪、生日、術後關心三個設定、同一位客人一週收到三封的情況。
 * 這是跨設定的安全網（生日提醒不受此限）。
 */
const GlobalCapCard = ({ businessId }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [cap, setCap] = useState({
    globalCapEnabled: true,
    globalCapCount: 3,
    globalCapDays: 30
  })

  const load = useCallback(async () => {
    if (!businessId) return
    try {
      const res = await api.get(
        `/api/business_B/${businessId}/revisit-reminder-settings`
      )
      if (res?.data?.data) setCap(res.data.data)
    } catch (err) {
      // 讀不到就用預設值顯示，不擋住整頁
      console.warn('取得回訪提醒設定失敗:', err?.message)
    } finally {
      setLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    load()
  }, [load])

  const save = async (next) => {
    setCap(next)
    setSaving(true)
    try {
      await api.put(
        `/api/business_B/${businessId}/revisit-reminder-settings`,
        next
      )
      message.success('已更新發送上限')
    } catch (err) {
      message.error('更新失敗，請稍後再試')
      load()
    } finally {
      setSaving(false)
    }
  }

  if (loading) return null

  return (
    <Card size='small' style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <span style={{ fontWeight: 600 }}>發送上限</span>
        <Switch
          checked={cap.globalCapEnabled}
          loading={saving}
          onChange={(checked) =>
            save({ ...cap, globalCapEnabled: checked })
          }
          checkedChildren='開'
          unCheckedChildren='關'
        />
        {cap.globalCapEnabled && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap'
            }}
          >
            同一位客人
            <InputNumber
              size='small'
              min={1}
              max={365}
              value={cap.globalCapDays}
              onChange={(v) =>
                v && save({ ...cap, globalCapDays: Number(v) })
              }
              style={{ width: 70 }}
            />
            天內最多收到
            <InputNumber
              size='small'
              min={1}
              max={20}
              value={cap.globalCapCount}
              onChange={(v) =>
                v && save({ ...cap, globalCapCount: Number(v) })
              }
              style={{ width: 60 }}
            />
            封
          </span>
        )}
        <span style={{ color: '#86868b', fontSize: 12 }}>
          不論你開了幾個提醒設定都一起計算；生日提醒不受此限。
        </span>
      </div>
    </Card>
  )
}

const RevisitReminderPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded, isBrandPlan } = useMembership()
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(false)
  const [previewModalVisible, setPreviewModalVisible] = useState(false)
  const [sendModalVisible, setSendModalVisible] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [sendResult, setSendResult] = useState(null)
  const [sending, setSending] = useState(false)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  )

  const businessId = user?.businessId

  // 檢測手機寬度（沿用專案既有 <=768px 斷點做法）
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // 所有 Hooks 必须在早期返回之前调用
  useEffect(() => {
    if (businessId && permissionsLoaded && isBrandPlan()) {
      fetchConfigs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, permissionsLoaded, isBrandPlan])

  const fetchConfigs = async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const response = await getRevisitReminderConfigs(businessId)
      if (response.data.success) {
        setConfigs(response.data.data || [])
      }
    } catch (error) {
      console.error('獲取配置列表失敗:', error)
      message.error('獲取配置列表失敗')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    navigate('/customers/revisit-reminders/new')
  }

  const handleEdit = (config) => {
    navigate(`/customers/revisit-reminders/${config._id}`)
  }

  const handleDelete = async (configId) => {
    if (!businessId) return
    try {
      const response = await deleteRevisitReminderConfig(businessId, configId)
      if (response.data.success) {
        message.success('刪除成功')
        fetchConfigs()
      }
    } catch (error) {
      console.error('刪除配置失敗:', error)
      message.error('刪除配置失敗')
    }
  }

  const handlePreview = async (configId) => {
    if (!businessId) return
    setLoading(true)
    try {
      const response = await previewRevisitReminderRecipients(
        businessId,
        configId
      )
      if (response.data.success) {
        setPreviewData(response.data.data)
        setPreviewModalVisible(true)
      }
    } catch (error) {
      console.error('預覽失敗:', error)
      message.error('預覽失敗')
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async (configId, dryRun = false) => {
    if (!businessId) return
    setSending(true)
    try {
      const response = await sendRevisitReminder(businessId, configId, dryRun)
      if (response.data.success) {
        setSendResult(response.data.data)
        setSendModalVisible(true)
        if (!dryRun) {
          message.success('發送完成')
          fetchConfigs()
        }
      }
    } catch (error) {
      console.error('發送失敗:', error)
      message.error('發送失敗')
    } finally {
      setSending(false)
    }
  }

  const columns = [
    {
      title: '配置名稱',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '狀態',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? '啟用' : '停用'}
        </Tag>
      )
    },
    {
      title: '提醒類型',
      dataIndex: ['timing', 'type'],
      key: 'type',
      render: (type) => (
        <Tag color={TYPE_COLOR[type] || 'default'}>
          {TYPE_LABEL[type] || type}
        </Tag>
      )
    },
    {
      title: '統計',
      key: 'stats',
      render: (_, record) => {
        const stats = record.stats || {}
        return (
          <Space>
            <span>已發送: {stats.totalSent || 0}</span>
            <span>成功: {stats.successCount || 0}</span>
            <span>失敗: {stats.failedCount || 0}</span>
          </Space>
        )
      }
    },
    {
      title: '最後發送時間',
      dataIndex: ['stats', 'lastSentAt'],
      key: 'lastSentAt',
      // toLocaleString('zh-TW') 只設語系、不設時區，值還是跟著裝置跑
      render: (date) => formatTz(date, 'YYYY/MM/DD HH:mm', '尚未發送')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record._id)}
          >
            預覽
          </Button>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編輯
          </Button>
          <Button
            type='link'
            icon={<SendOutlined />}
            onClick={() => handleSend(record._id, false)}
            loading={sending}
          >
            發送
          </Button>
          <Popconfirm
            title='確定要刪除這個配置嗎？'
            onConfirm={() => handleDelete(record._id)}
            okText='確定'
            cancelText='取消'
          >
            <Button type='link' danger icon={<DeleteOutlined />}>
              刪除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  // 手機版：把寬表格改成卡片式清單，欄位以「標籤：值」直式排列，避免文字被壓成直排、時間被裁切
  const renderMobileList = () => (
    <Spin spinning={loading}>
      {configs.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '40px 0' }}>
          尚無提醒配置
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {configs.map((config) => {
            const stats = config.stats || {}
            const type = config.timing?.type
            const lastSent = formatTz(
              stats.lastSentAt,
              'YYYY/MM/DD HH:mm',
              '尚未發送'
            )
            return (
              <Card key={config._id} size='small'>
                {/* 標題：名稱自成一行（正常橫排、可換行），標籤在其下一行，避免窄螢幕溢出 */}
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    wordBreak: 'break-word'
                  }}
                >
                  {config.name}
                </div>
                <div style={{ marginTop: 6 }}>
                  <Space size={4} wrap>
                    <Tag
                      color={config.isEnabled ? 'green' : 'default'}
                      style={{ marginRight: 0 }}
                    >
                      {config.isEnabled ? '啟用' : '停用'}
                    </Tag>
                    <Tag
                      color={TYPE_COLOR[type] || 'default'}
                      style={{ marginRight: 0 }}
                    >
                      {TYPE_LABEL[type] || type}
                    </Tag>
                  </Space>
                </div>

                {/* 統計：橫向排列、清楚不擠 */}
                <div
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px 16px',
                    fontSize: 14
                  }}
                >
                  <span style={{ color: '#8c8c8c' }}>統計：</span>
                  <span>
                    已發送 <b>{stats.totalSent || 0}</b>
                  </span>
                  <span style={{ color: '#3f8600' }}>
                    成功 <b>{stats.successCount || 0}</b>
                  </span>
                  <span style={{ color: '#cf1322' }}>
                    失敗 <b>{stats.failedCount || 0}</b>
                  </span>
                </div>

                {/* 最後發送時間：完整顯示、不裁切 */}
                <div style={{ marginTop: 6, fontSize: 14 }}>
                  <span style={{ color: '#8c8c8c' }}>最後發送：</span>
                  <span>{lastSent}</span>
                </div>

                {/* 操作：可換行 */}
                <div
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 4,
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: 8
                  }}
                >
                  <Button
                    size='small'
                    type='link'
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(config._id)}
                  >
                    預覽
                  </Button>
                  <Button
                    size='small'
                    type='link'
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(config)}
                  >
                    編輯
                  </Button>
                  <Button
                    size='small'
                    type='link'
                    icon={<SendOutlined />}
                    onClick={() => handleSend(config._id, false)}
                    loading={sending}
                  >
                    發送
                  </Button>
                  <Popconfirm
                    title='確定要刪除這個配置嗎？'
                    onConfirm={() => handleDelete(config._id)}
                    okText='確定'
                    cancelText='取消'
                  >
                    <Button
                      size='small'
                      type='link'
                      danger
                      icon={<DeleteOutlined />}
                    >
                      刪除
                    </Button>
                  </Popconfirm>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </Spin>
  )

  /* 三種提醒類型的顯示名稱與顏色（列表與手機卡片共用，避免兩邊寫法不同步） */
  const TYPE_LABEL = {
    revisit: '再回訪提醒',
    followup: '貼心提醒',
    broadcast: '節慶／公告'
  }
  const TYPE_COLOR = {
    revisit: 'blue',
    followup: 'purple',
    broadcast: 'gold'
  }

  // 預覽收件人：欄位定義（桌機表格與手機卡片共用同一份，避免兩邊不同步）
  const previewColumns = [
    {
      title: '客戶姓名',
      dataIndex: 'customerName',
      key: 'customerName'
    },
    {
      title: '會員等級',
      dataIndex: 'membershipLevel',
      key: 'membershipLevel'
    },
    {
      title: '標籤',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) =>
        tags && tags.length > 0
          ? tags.map((tag) => (
              <Tag key={tag} style={{ marginRight: 4 }}>
                {tag}
              </Tag>
            ))
          : '-'
    },
    {
      title: '最後訪問日期',
      dataIndex: 'lastVisitDate',
      key: 'lastVisitDate'
    },
    {
      title: '服務名稱',
      dataIndex: 'serviceName',
      key: 'serviceName'
    }
  ]

  // 發送結果：欄位定義（桌機表格與手機卡片共用同一份）
  const sendResultColumns = [
    {
      title: '客戶姓名',
      dataIndex: ['recipient', 'customerName'],
      key: 'customerName'
    },
    {
      title: '發送狀態',
      dataIndex: 'success',
      key: 'success',
      render: (success) => (
        <Tag color={success ? 'green' : 'red'}>{success ? '成功' : '失敗'}</Tag>
      )
    },
    {
      title: '發送方式',
      dataIndex: 'via',
      key: 'via'
    },
    {
      title: '錯誤訊息',
      dataIndex: 'error',
      key: 'error',
      render: (error) => error || '-'
    }
  ]

  // 依 antd Table 的 dataIndex 取值，支援 ['recipient', 'customerName'] 這種路徑陣列
  const getColumnValue = (record, dataIndex) =>
    Array.isArray(dataIndex)
      ? dataIndex.reduce((acc, key) => (acc == null ? acc : acc[key]), record)
      : record?.[dataIndex]

  // 手機版：把寬表格改成卡片式清單，欄位以「標籤：值」直式排列。
  // 表格在窄螢幕會把欄寬壓到剩一個字，導致中文被壓成直排、右側欄位被裁切。
  const renderMobileCardList = (columns, dataSource, { rowKey, emptyText }) => {
    if (!dataSource || dataSource.length === 0) {
      return (
        <div
          style={{ textAlign: 'center', color: '#8c8c8c', padding: '24px 0' }}
        >
          {emptyText}
        </div>
      )
    }

    return (
      <List
        dataSource={dataSource}
        rowKey={rowKey}
        split={false}
        pagination={
          dataSource.length > 5 ? { pageSize: 5, size: 'small' } : false
        }
        renderItem={(item, index) => (
          <List.Item style={{ padding: '0 0 12px 0', border: 'none' }}>
            <Card size='small' style={{ width: '100%' }}>
              {columns.map((col) => {
                const raw = getColumnValue(item, col.dataIndex)
                const value = col.render
                  ? col.render(raw, item, index)
                  : raw === undefined || raw === null || raw === ''
                    ? '-'
                    : raw
                return (
                  <div
                    key={col.key}
                    style={{
                      display: 'flex',
                      gap: 6,
                      fontSize: 14,
                      lineHeight: 1.6,
                      marginBottom: 4
                    }}
                  >
                    <span style={{ color: '#8c8c8c', flex: '0 0 auto' }}>
                      {col.title}：
                    </span>
                    <span
                      style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {value}
                    </span>
                  </div>
                )
              })}
            </Card>
          </List.Item>
        )}
      />
    )
  }

  // 权限检查 - 在 return 中处理，而不是早期返回
  if (!permissionsLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size='large' tip='正在載入權限...' />
      </div>
    )
  }

  if (!isBrandPlan()) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Card>
          <h2>功能限制</h2>
          <p>此功能僅限品牌方案用戶使用，請升級您的會員方案。</p>
        </Card>
      </div>
    )
  }

  /* 設定與紀錄分成兩個分頁：設定那頁本來就長，把紀錄接在下面會變成一直捲。
     Sidebar 不另外開入口，店家找「回訪提醒」的心智路徑不變。 */
  const settingsTab = (
    <>
      <GlobalCapCard businessId={businessId} />
      <Card>
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 12 : 0,
            marginBottom: '24px'
          }}
        >
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : undefined }}>
            回訪提醒配置管理
          </h2>
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchConfigs}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              新增提醒配置
            </Button>
          </Space>
        </div>

        {isMobile ? (
          renderMobileList()
        ) : (
          <Table
            columns={columns}
            dataSource={configs}
            rowKey='_id'
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 條記錄`
            }}
          />
        )}
      </Card>
    </>
  )

  return (
    <div style={{ padding: '24px' }}>
      <Tabs
        defaultActiveKey='configs'
        items={[
          { key: 'configs', label: '提醒設定', children: settingsTab },
          {
            key: 'logs',
            label: '發送紀錄',
            children: (
              <RevisitReminderLogs
                businessId={businessId}
                configs={configs}
              />
            )
          }
        ]}
      />

      {/* 預覽模態框 */}
      <Modal
        title='預覽收件人'
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key='close' onClick={() => setPreviewModalVisible(false)}>
            關閉
          </Button>
        ]}
        width={isMobile ? '94vw' : 800}
        style={isMobile ? { top: 16, maxWidth: '94vw', padding: 0 } : undefined}
      >
        {previewData && (
          <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={isMobile ? 12 : 8}>
                <Statistic
                  title='這次會發給'
                  value={previewData.totalCount || 0}
                  suffix='人'
                />
              </Col>
              {previewData.excludedCount > 0 && (
                <Col span={isMobile ? 12 : 8}>
                  <Statistic
                    title='符合條件但不會發'
                    value={previewData.excludedCount}
                    suffix='人'
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
              )}
            </Row>

            {/* 少發了誰、為什麼，要在按下發送之前就看得到 */}
            {previewData.excludedCount > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Hint tone='warning' title={`有 ${previewData.excludedCount} 位符合條件的客人不會收到`}>
                  <List
                    size='small'
                    dataSource={previewData.excluded || []}
                    renderItem={(item) => (
                      <List.Item style={{ padding: '4px 0', border: 'none' }}>
                        <span>
                          <b>{item.customerName || '（未留姓名）'}</b>
                          　{item.detail}
                        </span>
                      </List.Item>
                    )}
                  />
                </Hint>
              </div>
            )}
            <Divider />
            {isMobile ? (
              renderMobileCardList(
                previewColumns,
                previewData.recipients || [],
                {
                  rowKey: (item) => item.customerId,
                  emptyText: '沒有符合條件的收件人'
                }
              )
            ) : (
              <Table
                dataSource={previewData.recipients || []}
                rowKey='customerId'
                pagination={{ pageSize: 10 }}
                columns={previewColumns}
              />
            )}
          </div>
        )}
      </Modal>

      {/* 發送結果模態框 */}
      <Modal
        title='發送結果'
        open={sendModalVisible}
        onCancel={() => setSendModalVisible(false)}
        footer={[
          <Button key='close' onClick={() => setSendModalVisible(false)}>
            關閉
          </Button>
        ]}
        width={isMobile ? '94vw' : 800}
        style={isMobile ? { top: 16, maxWidth: '94vw', padding: 0 } : undefined}
      >
        {sendResult && (
          <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={8}>
                <Statistic
                  title='總收件人數'
                  value={sendResult.totalRecipients || 0}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title='成功數'
                  value={sendResult.successCount || 0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title='失敗數'
                  value={sendResult.failedCount || 0}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
            </Row>
            <Divider />
            {isMobile ? (
              renderMobileCardList(sendResultColumns, sendResult.results || [], {
                rowKey: (record, index) => index,
                emptyText: '沒有發送記錄'
              })
            ) : (
              <Table
                dataSource={sendResult.results || []}
                rowKey={(record, index) => index}
                pagination={{ pageSize: 10 }}
                columns={sendResultColumns}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default RevisitReminderPage
